import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  NavLink,
  Spinner,
  SpanWithData,
  CardTable as EmployerPayrollCycleTable
} from '../'

import {
  decodeBase64
} from '../../utils'

export class EmployerPayrollCycles extends React.Component {
  static propTypes = {
    employer: PropTypes.string.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = {
    onFirstPage: true,
    onLastPage: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if componenent's loading prop changes from 'true' to 'false',
    if (this.props.loading && !nextProps.loading) {
      // and there is no next page, we're on the first AND last results page
      if (!nextProps.payrollcycles.pageInfo.hasNextPage) {
        this.setState({
          onLastPage: true,
          onFirstPage: true
        })
      } else {  // else we're on the first results page
        this.setState({
          onLastPage: false,
          onFirstPage: true
        })
      }
    }
  }

  // *** Note:
  // hasPreviousPage is only meaningful when last is included, as it is always
  // false otherwise. hasNextPage is only meaningful when first is included,
  // as it is always false otherwise.

  handleNextPage = () => {
    if (this.props.payrollcycles.pageInfo.hasNextPage) {
      // going forward, has next
      this.props.loadNextEmployerPayrollCycles(this.props.employer)
      .then((responseObj) => {
        if (!responseObj.data.payrollcycles.pageInfo.hasNextPage) {
          this.setState({
            onLastPage: true,
            onFirstPage: false // not guaranteed; might very well be on first page
          })
        }
        else {
          this.setState({ onFirstPage: false })
        }
      })
    } else {
      // changing direction: now going forward
      this.props.loadNextEmployerPayrollCycles(this.props.employer)
      .then((responseObj) => {
        if (!responseObj.data.payrollcycles.pageInfo.hasNextPage) {
            this.setState({
              onLastPage: true,
              onFirstPage: false, // not guaranteed; might be
            })
        }
        else {
          this.setState({ onFirstPage: false })
        }
      })
    }
  }

  handlePreviousPage = () => {
    if (this.props.payrollcycles.pageInfo.hasPreviousPage) {
      // going backward, has previous
      this.props.loadPreviousEmployerPayrollCycles(this.props.employer)
      .then((responseObj) => {
        if (!responseObj.data.payrollcycles.pageInfo.hasPreviousPage) {
          this.setState({
            onFirstPage: true,
            onLastPage: false  // not guaranteed; might be...
          })
        }
        else {
          this.setState({ onLastPage: false })
        }
      })
    } else {
      // changing direction: now going backward
      this.props.loadPreviousEmployerPayrollCycles(this.props.employer)
      .then((responseObj) => {
        if (!responseObj.data.payrollcycles.pageInfo.hasPreviousPage) {
          this.setState({
            onFirstPage: true,
            onLastPage: false  // not guaranteed; might be...
          })
        }
        else {
          this.setState({ onLastPage: false })
        }
      })
    }
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map((row, i) => (
      <EmployerPayrollCycleTableDataRow data={row} employer={this.props.employer} key={i}/>
    ))

    return rows
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    } else {
      let rows = this.generateTableDataRows(this.props.payrollcycles.edges)

      return (
        <div className={this.props.className}>
          <EmployerPayrollCycleTable
            heading="Employer payroll cycles:"
            headingIcon="groups"
            columns={["Name", "Cycle"]}
            tableDataRows={rows}
            nextPage={this.handleNextPage}
            previousPage={this.handlePreviousPage}
            onFirstPage={this.state.onFirstPage}
            onLastPage={this.state.onLastPage}
            navigationButtons="header"
          />
        </div>
      )
    }
  }
}

const EmployerPayrollCycleTableDataRow = (props) => {
  let row = props.data.node

  return (
    <tr className="slds-hint-parent">
      <th scope="row">
        <div className="slds-truncate" title={row.name}>
          <NavLink to={`/os/identity/${row.employer.identity.id}/payrollcycles/${row.id}`}>
            {row.name}
          </NavLink>
        </div>
      </th>
      <td role="gridcell">
        <div className="slds-truncate" title={row.cycle}>
          <SpanWithData forField="PayrollCycleCycle" searchKey={row.cycle} />
        </div>
      </td>
    </tr>
  )
}

const getEmployerPayrollCycles = gql`query($id: UUID) {
    payrollcycles(employer_Id: $id, first: 10) {
      edges {
        node {
          ...PayrollCycle
          employer {
            id
            identity {
              id
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.payrollcycle}
`

const getPreviousEmployerPayrollCycles = gql`query($id: UUID, $cursor: String) {
    payrollcycles(employer_Id: $id, last: 10, before: $cursor) {
      edges {
        node {
          ...PayrollCycle
          employer {
            id
            identity {
              id
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.payrollcycle}
`

const withData = graphql(getEmployerPayrollCycles, {
  options: ({ employer }) => ({
    variables: {
      id: decodeBase64(employer)
    }
  }),
  props: ({data: { loading, payrollcycles, fetchMore }}) => ({
    loading,
    payrollcycles,
    loadNextEmployerPayrollCycles: (employer) => {
      return fetchMore({
        query: getEmployerPayrollCycles,
        variables: {
          id: employer,
          cursor: payrollcycles.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.payrollcycles.edges
          const pageInfo = fetchMoreResult.payrollcycles.pageInfo

          return {
            payrollcycles: {
              __typename: previousResult.payrollcycles.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    loadPreviousEmployerPayrollCycles: (employer) => {
      return fetchMore({
        query: getPreviousEmployerPayrollCycles,
        variables: {
          id: employer,
          cursor: payrollcycles.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.payrollcycles.edges
          const pageInfo = fetchMoreResult.payrollcycles.pageInfo

          return {
            payrollcycles: {
              __typename: previousResult.payrollcycles.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(EmployerPayrollCycles)



// WEBPACK FOOTER //
// ./src/components/employer-payrollcycles/index.js